import { setupClickTracking } from './clickTracking';
import { DEFAULT_CONFIG, MONITOR_PAGE } from './constants';
import { getDeviceInfo, getFingerprint } from './device';
import { extractError, parseStackTrace, trackJSError } from './errorTracking';
import { filterRoute, getPageTitle, handlePageView } from './pageTracking';
import type { Routes, TrackingConfig, TrackingEventData, TrackType } from './types';
import { TrackingEventType } from './types';
import { addQueueData, delQueueItem, flush, getQueueData, log } from './utils';
/**
 * 埋点SDK类
 */
class TrackingSDK {
  // 配置项
  private config: TrackingConfig;
  private readonly storageName = 'trackQueue';
  // 数据存储
  private eventQueue: TrackingEventData[] = getQueueData(this.storageName) || [];
  // 用户id
  private userId?: string;
  // 是否初始化
  private isInitialized = false;
  // 当前打开的页面
  private currentPagePath = '';
  // 页面开始时间
  private pageStartTime = 0;
  // 浏览器指纹
  private readonly fingerprint = getFingerprint();
  // 路由数据
  private routeList: Routes[] = [];
  // 页面标题
  private pageTitle?: string = '';
  // 记录上一个页面路径
  private previousPagePath = '';
  // 查询功能中的key
  private dictKey?: string = '';
  private productName?: string = '';
  // 拦截console.error
  private readonly originalConsole = console.error;
  // 环境变量
  private readonly env?: string = import.meta.env.MODE;
  // 当接口错误延迟上报时间，5分钟
  private readonly apiErrDelayedTime?: number = 1000 * 60 * 5;
  // 当接口上报错误定时器
  private apiErrTimer?: NodeJS.Timeout | number = 0;
  // 接口错误控制
  private apiError?: boolean = false;
  // 页面关闭或者刷新
  private unloadPage?: boolean = false;
  constructor(config: TrackingConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * 初始化埋点SDK
   * @param config 配置项
   */
  init(config: TrackingConfig = {}): void {
    if (this.isInitialized) {
      log(this.config.debug, 'SDK已经初始化，请勿重复初始化');
      return;
    }
    this.config = { ...this.config, ...config };
    this.isInitialized = true;
    this.routeList = this.config.routerList ?? [];
    this.userId = this.config.userId || '';
    if (!this.config.enabled) {
      log(this.config.debug, '埋点SDK已禁用');
      return;
    }
    log(this.config.debug, '埋点SDK初始化成功');
    this.queueDataFlush();
    // 自动记录页面浏览
    if (this.config.autoPageView) {
      this.trackPageView();
      // 监听路由变化
      this.setupRouteTracking();
    }

    // 自动记录点击事件
    if (this.config.autoClickTracking) {
      this.setupClickTracking();
    }

    // 页面卸载时上报剩余数据
    window.addEventListener('unload', () => {
      this.unloadPage = true;
      this.trackPageView({ reload: this.unloadPage });
      this.destroy();
    });

    // 监听全局JavaScript错误
    window.addEventListener('error', (event) => {
      this.trackJSError(event.error || event.message);
    });

    // 监听Promise rejected错误
    window.addEventListener('unhandledrejection', (event) => {
      this.trackJSError(event.reason);
    });

    // 拦截console.error
    this.interceptConsoleError();

    // 拦截React错误
    this.interceptReactError();

    // 拦截资源加载错误
    this.interceptResourceError();

    log(this.config.debug, '埋点SDK已开启全部监听');
  }

  /**
   * 设置用户ID
   * @param userId 用户ID
   */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * 设置路由数据
   * @param list
   */
  setRouteList(list: Routes[]): void {
    this.routeList = list;
  }
  /**
   * 是否初始化
   * @returns
   */
  isInit() {
    return this.isInitialized;
  }

  /**
   * 手动埋点
   * @param data 事件数据
   */
  async track(data: TrackType): Promise<void> {
    if (!this.config.enabled || !this.isInitialized) return;
    const { pathname } = location;
    const { search } = location;
    let eventData: TrackingEventData = {
      ...data,
      pageTitle: data.previousPagePath
        ? getPageTitle(data.previousPagePath, this.routeList)
        : (data.pageTitle ?? getPageTitle(data.pagePath || pathname, this.routeList)),
      pagePath: data.previousPagePath || data.pagePath || `${pathname}${search}`,
      timestamp: Date.now(),
      userId: this.userId,
      fingerprint: this.fingerprint,
      ramdomId: Math.random().toString(36).substring(2, 15) + Date.now(),
    };
    if (!eventData.pageTitle) {
      const routeItem = filterRoute(data.pagePath!);
      eventData.pageTitle = routeItem?.label || routeItem?.meta?.name || '';
      if (!eventData.dictKey) {
        eventData.dictKey = routeItem?.queryDictKey;
        eventData.productName = routeItem?.name;
      }
    }

    if (!eventData.pageTitle) {
      this.currentPagePath = '';
      this.pageStartTime = 0;
      return;
    }
    if (data.previousPagePath !== data.pagePath) {
      this.previousPagePath = '';
    }
    if (
      !MONITOR_PAGE.some((item) => eventData.pagePath!.startsWith(item)) &&
      (data.eventType === TrackingEventType.PageStay ||
        data.eventType === TrackingEventType.PageView)
    )
      return;
    if (this.config.recordDeviceInfo) {
      eventData = {
        ...eventData,
        ...getDeviceInfo(),
      };
    }
    log(this.config.debug, '埋点事件已添加:', eventData);
    // 当有队列数据或者接口报错时候，不请求接口，把数据存储到队列中
    if (this.eventQueue.length || this.apiError) {
      addQueueData(this.storageName, eventData);
      this.eventQueue = getQueueData(this.storageName);
      return;
    }
    this.reportControl(eventData);
  }

  /**
   * JavaScript错误埋点
   * @param error 错误对象
   * @param errorMsg 错误描述
   */
  async trackJSError(error: Error | string | any[], errorMsg?: string): Promise<void> {
    await trackJSError(error, errorMsg, this.config.sourceMapEnable, this.env, (data) =>
      this.track({
        eventType: TrackingEventType.JsError,
        eventName: 'js_error',
        sysReportErrorInfoDto: data.sysReportErrorInfoDto,
      }),
    );
  }

  /**
   * API请求错误埋点
   * @param url 请求URL
   * @param method 请求方法
   * @param status 响应状态码
   * @param errorMessage 错误消息
   * @param responseData 响应数据
   * @param params 请求参数
   */
  trackApiError(
    url: string,
    method: string,
    status: number,
    errorMessage: string,
    responseData?: any,
    params?: any,
  ): void {
    const resData = {
      url,
      method,
      status,
      params,
    };
    let resDataStr = '';
    if (typeof responseData === 'string') {
      resDataStr = JSON.stringify(resData) + responseData;
    } else {
      resDataStr = JSON.stringify({
        ...resData,
        ...responseData,
      });
    }
    this.track({
      eventType: TrackingEventType.ApiError,
      eventName: 'api_error',
      sysReportErrorInfoDto: {
        message: errorMessage,
        stack: resDataStr,
        type: 'API Error',
        url,
        method,
        status,
        params,
        env: this.env,
      },
    });
  }

  /**
   * 页面浏览埋点
   * @param data 页面数据
   */
  trackPageView(
    data?: Partial<
      Pick<TrackingEventData, 'pagePath' | 'dictKey' | 'productName' | 'pageTitle'> & {
        pagePath?: string;
        reload?: boolean;
      }
    >,
  ): void {
    const result = handlePageView(
      this.currentPagePath,
      this.pageStartTime,
      this.previousPagePath,
      this.pageTitle,
      this.dictKey,
      this.productName,
      data,
      (trackData) => this.track(trackData),
    );

    this.currentPagePath = result.currentPagePath;
    this.pageStartTime = result.pageStartTime;
    this.previousPagePath = result.previousPagePath;
    this.pageTitle = result.pageTitle;
    this.dictKey = result.dictKey;
    this.productName = result.productName;
  }

  /**
   * 设置路由变化监听
   */
  private setupRouteTracking(): void {
    // 监听popstate事件（浏览器前进后退）
    window.addEventListener('popstate', () => {
      this.trackPageView();
    });

    // 重写pushState和replaceState方法，以监听编程式导航
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      setTimeout(() => this.trackPageView(), 0);
    };

    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      setTimeout(() => this.trackPageView(), 0);
    };
  }

  /**
   * 设置点击事件监听
   */
  private setupClickTracking(): void {
    setupClickTracking((data) => this.track(data));
  }

  /**
   * 拦截console.error
   */
  private interceptConsoleError(): void {
    if (!this.config.enabled) return;
    console.error = (...args: any[]) => {
      // 1. 先执行原始 console.error
      this.originalConsole.apply(console, args);
      try {
        // 2. 从参数中提取错误信息
        const error = extractError(args);
        if (!error?.stack) return null;

        // 3. 解析堆栈信息
        const stackTrace = parseStackTrace(error.stack);
        if (!stackTrace.length) return null;

        // 4. 对每个堆栈帧解析 sourcemap
        this.trackJSError(stackTrace, args.toString());
      } catch (error) {
        console.warn('SourceMap 解析失败:', error);
      }
    };
  }

  /**
   * 拦截React错误
   */
  private interceptReactError(): void {
    if (!this.config.enabled) return;
    // 如果React存在，尝试拦截错误
    if (typeof window !== 'undefined' && (window as any).React) {
      const { React } = window as any;

      // 保存原始的createElement
      const originalCreateElement = React.createElement;

      // 重写createElement
      React.createElement = function (type: any, props: any, ...children: any[]) {
        try {
          return originalCreateElement.apply(this, [type, props, ...children]);
        } catch (error: unknown) {
          if (error instanceof Error) {
            this.trackJSError(error);
          } else {
            this.trackJSError(String(error));
          }
          throw error;
        }
      };
    }
  }

  /**
   * 拦截资源加载错误
   */
  private interceptResourceError(): void {
    if (!this.config.enabled) return;
    // 监听资源加载错误
    window.addEventListener(
      'error',
      (event) => {
        // 如果是资源加载错误
        if (event.target && (event.target as any).tagName) {
          const target = event.target as HTMLElement;
          const tagName = target.tagName.toLowerCase();
          const src = (target as any).src || (target as any).href;

          this.trackJSError(`Failed to load ${tagName}: ${src}`);
        }
      },
      true,
    );
  }

  /**
   * 日志输出
   */
  private log(...args: any[]): void {
    log(this.config.debug, ...args);
  }

  /**
   * 销毁SDK
   */
  private destroy(): void {
    this.isInitialized = false;
    clearInterval(this.apiErrTimer);
    this.apiErrTimer = 0;
    this.apiError = false;
    this.log('埋点SDK已销毁');
  }

  /**
   * 上报控制
   * @param data  上报的数据
   */
  private async reportControl(data: TrackingEventData) {
    let reportRes = false;
    if (this.config.endpoint) {
      reportRes = await flush(this.config.endpoint, data, (...args) =>
        log(this.config.debug, ...args),
      );
      // 页面不是刷新或者关闭窗口且接口异常
      if (!reportRes && !this.unloadPage) {
        // 接口出问题了, 把数据加入队列中
        addQueueData(this.storageName, data);
        this.apiError = true;
        this.openDelayedTime();
      } else {
        // 从数据中删除
        delQueueItem(this.storageName, data.ramdomId);
      }
      this.eventQueue = getQueueData(this.storageName);
    }
    return reportRes;
  }

  /**
   * 队列数据上传
   */
  private async queueDataFlush() {
    if (this.eventQueue.length) {
      this.log('开启队列上报');
      const firstItem = this.eventQueue[0];
      const fitstItemRes = await this.reportControl(firstItem);
      if (fitstItemRes) {
        // 上报剩余队列中的数据
        try {
          // 使用 Promise.all 并发上报所有队列数据
          const reportPromises = this.eventQueue.map((item) => this.reportControl(item));
          const results = await Promise.all(reportPromises);
          // 检查上报结果，如果全部成功则清空队列
          const allSuccess = results.every((res) => res === true);
          if (allSuccess) {
            // 清空队列
            this.log('队列数据上报成功');
            this.apiError = false;
            clearInterval(this.apiErrTimer);
            this.apiErrTimer = 0;
          }
        } catch (error) {
          console.log('队列数据上报失败:', error);
        }
        return;
      }
      this.log('第一条测试队列上报数据失败');
    }
  }

  /**
   * 开启延迟上报控制器
   */
  private async openDelayedTime() {
    if (this.apiErrTimer) return;
    this.apiErrTimer = setInterval(() => {
      this.queueDataFlush();
    }, this.apiErrDelayedTime);
  }
}

export { TrackingSDK };
